import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Loader2, ShieldCheck, AlertTriangle, RefreshCw, Database, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = window.location.origin + "/api";

const fmtAge = (sec) => {
  if (sec == null) return "—";
  if (sec < 60) return `${Math.round(sec)}s fa`;
  if (sec < 3600) return `${Math.round(sec / 60)}m fa`;
  if (sec < 86400) return `${(sec / 3600).toFixed(1)}h fa`;
  return `${(sec / 86400).toFixed(1)}g fa`;
};

const KEY_LABELS = {
  calendars: "Calendari GHL",
  doctors: "Medici (dropdown)",
  assistants: "Assistenti (dropdown)",
  insurances: "Assicurazioni (dropdown)",
  pipelines_full: "Pipeline + stages",
  open_opps: "Opportunità aperte",
  users_map: "Mappa utenti GHL",
};

/**
 * Admin panel: stato del layer di resilienza GHL → MongoDB.
 *
 * Per ogni cache critica mostra:
 *   - Età dello snapshot DB
 *   - Numero di elementi
 *   - Allarme se la snapshot è > 10 min vecchia (fail dello scheduler q5min)
 *
 * Permette di forzare un refresh immediato (utile dopo un down GHL prolungato).
 */
export default function GhlResiliencePanel() {
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState(null);

  const fetchStatus = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data: resp } = await axios.get(`${API}/admin/ghl-resilience-status`, {
        withCredentials: true,
      });
      setData(resp);
    } catch (err) {
      if (!silent) toast.error(err?.response?.data?.detail || "Errore caricamento stato resilienza");
    } finally {
      setLoading(false);
    }
  }, []);

  const forceRefresh = async () => {
    setRefreshing(true);
    try {
      await axios.post(`${API}/admin/ghl-resilience-refresh`, {}, { withCredentials: true });
      toast.success("Snapshot GHL aggiornati con successo");
      await fetchStatus(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore refresh snapshot");
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchStatus(false);
    // Auto-refresh ogni 60s per vedere il job APScheduler in azione.
    const id = setInterval(() => fetchStatus(true), 60000);
    return () => clearInterval(id);
  }, [fetchStatus]);

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4 space-y-3" data-testid="ghl-resilience-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center">
            <ShieldCheck className="w-4 h-4 text-indigo-600" />
          </div>
          <div>
            <div className="text-sm font-bold text-slate-800">Resilienza GHL</div>
            <div className="text-[10px] text-slate-500">Cache MongoDB fallback (refresh q5min)</div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="sm"
            onClick={forceRefresh}
            disabled={refreshing || loading}
            className="h-7 px-2 text-[11px]"
            data-testid="ghl-resilience-force-refresh-btn"
            title="Forza refresh immediato delle snapshot"
          >
            {refreshing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />}
            <span className="ml-1">Refresh ora</span>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => fetchStatus(false)}
            disabled={loading}
            className="h-7 px-2"
            data-testid="ghl-resilience-reload-btn"
            title="Ricarica stato"
          >
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          </Button>
        </div>
      </div>

      {!data && loading && (
        <div className="text-xs text-slate-500 flex items-center gap-2">
          <Loader2 className="w-3 h-3 animate-spin" />
          Caricamento…
        </div>
      )}

      {data && (
        <div className="space-y-2 text-xs">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {data.snapshots.map((s) => {
              const isStale = s.present && s.age_seconds != null && s.age_seconds > 600; // > 10 min
              const tone = !s.present
                ? "bg-red-50 border-red-200 text-red-800"
                : isStale
                ? "bg-amber-50 border-amber-200 text-amber-800"
                : "bg-emerald-50 border-emerald-200 text-emerald-800";
              return (
                <div key={s.key} className={`${tone} border rounded-lg p-2 flex items-start justify-between gap-2`}>
                  <div className="min-w-0">
                    <div className="font-bold text-[12px] truncate">
                      {KEY_LABELS[s.key] || s.key}
                    </div>
                    <div className="text-[10px] opacity-80">
                      {s.present ? (
                        <>
                          {s.size != null && <span>{s.size} items · </span>}
                          <span>{fmtAge(s.age_seconds)}</span>
                          {s.source && <span> · {s.source}</span>}
                        </>
                      ) : (
                        <span>Mai persistito</span>
                      )}
                    </div>
                  </div>
                  {!s.present || isStale ? (
                    <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  ) : (
                    <ShieldCheck className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  )}
                </div>
              );
            })}
          </div>

          <div className="flex items-center justify-between text-[10px] text-slate-500 pt-1">
            <span className="flex items-center gap-1">
              <Database className="w-3 h-3" />
              Snapshot eventi giornalieri: <strong>{data.events_snapshots_count}</strong>
              {" · "}
              dashboard: <strong>{data.dashboard_snapshots_count || 0}</strong>
            </span>
            <span>{data.scheduler_job}</span>
          </div>
          <div className="text-[10px] text-slate-500 italic">
            Quando GHL va in down (succede 1-2× a settimana), questi snapshot mantengono la UI operativa.
            Refresh automatico ogni 5 minuti dal job <code>ghl_resilient_sync</code>.
          </div>
        </div>
      )}
    </div>
  );
}
